import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  AgreementContratStatus,
  Agreements,
  Companies,
  Invoices,
  ShipmentFulfillments,
} from '@etoh/database/core';
import { convertCurrencyToUSD } from 'apps/desktop/src/app/currenciesConversions/pure';
import {
  formatCurrency,
  formatPercent,
} from 'apps/desktop/src/app/locales/locales';
import { UserService } from 'apps/desktop/src/app/user.service';
import { startOfYear } from 'date-fns';
import { isNumber, sum } from 'lodash';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  combineLatest,
  map,
  Observable,
  shareReplay,
  startWith,
  take,
} from 'rxjs';
import { AgreementsService } from '../../../agreements/agreements.service';
import { ListComponent } from '../../../shared/components/list/list.component';
import {
  currencyFormatter,
  dateFormatter,
  getValueForOptions,
} from '../../../shared/components/table/formatter';
import { agreementsContractStatusOptions } from '../../../shared/forms/options';
import { Context } from '../../../shared/list.interfaces';
import { StoreService } from '../../../store.service';
import { UsersService } from '../../../users.service';

interface Base {
  id: number;
  buyerBrokerCommission: number;
  sellerBrokerCommission: number;
  brokerCommission: number;
  deferredRevenue: number;
  invoiced: number;
  currency: string;
  agreement: Agreements;
  completion: number | undefined;
}

interface AgreementsRow extends Base {
  buyerCompany: number;
  sellerCompany: number;
  buyerBroker: string;
  sellerBroker: string;
  status: AgreementContratStatus;
  startDate: string;
}

interface CompaniesRow extends Base {
  company: number;
  targetInUsd: number | null;
}

@Component({
  selector: 'etoh-agreements',
  standalone: true,
  imports: [
    CommonModule,
    ListComponent,
    ReactiveFormsModule,
    NzDatePickerModule,
    FlexLayoutModule,
    NzFormModule,
    NzSelectModule,
  ],
  template: `
    @if (usersOptions$ | async; as usersOptions) {

    <section fxLayout="column">
      <div>
        <div fxLayout="row" fxLayoutGap="4px">
          <span fxFlex></span>
          <div>
            <form nz-form nzLayout="vertical">
              <nz-form-item>
                <nz-select
                  [formControl]="selectedUserId"
                  nzShowSearch
                  nzPlaceHolder="Select the user"
                  name="selectedUser"
                >
                  @for (option of usersOptions; track option.value) {
                  <nz-option
                    [nzLabel]="option.label"
                    [nzValue]="option.value"
                  ></nz-option>
                  }
                </nz-select>
              </nz-form-item>
            </form>
          </div>

          <div>
            <nz-range-picker [formControl]="dateFormControls"></nz-range-picker>
          </div>
        </div>
      </div>

      @if (columnDefsDisplayed$ | async; as columnDefsDisplayed) {
      <etoh-list
        [dataType]="'agreements'"
        [data$]="data$"
        [context]="context"
        [columnDefsDisplayed]="columnDefsDisplayed"
      ></etoh-list>
      }
    </section>
    } @else { Loading }
  `,
  styleUrl: './agreements.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgreementsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  currentRoute$: Observable<'agreements' | 'companies'> = this.route.url.pipe(
    map((url) => {
      if (url[0].path === 'companies') {
        return 'companies';
      }
      return 'agreements';
    })
  );

  dateFormControls = new FormControl<null | [Date, Date]>([
    startOfYear(new Date()),
    new Date(),
  ]);

  private agreementsService = inject(AgreementsService);
  private storeService = inject(StoreService);

  private userService = inject(UserService);

  private usersService = inject(UsersService);

  usersOptions$ = this.usersService.getBrokersForOptions$();

  columnDefsDisplayed$: Observable<any[]> = this.currentRoute$.pipe(
    map((currentRoute) => {
      const getDeferredRevenue = (
        invoiced: number,
        realDeferredRevenue: number,
        totalRevenue: number,
        completion = 1
      ) => {
        const subTotalRevenue = 0.9 * totalRevenue;
        if (completion >= 1 && invoiced > subTotalRevenue) {
          return 0;
        }

        return realDeferredRevenue;
      };

      if (currentRoute === 'agreements') {
        return [
          {
            field: 'id',
            filter: 'agNumberColumnFilter',
          },
          {
            field: 'buyerCompany',
            dataType: 'companies',
          },
          {
            field: 'sellerCompany',
            dataType: 'companies',
          },
          {
            field: 'status',
            valueGetter: (params: any) =>
              getValueForOptions(
                'contractStatus',
                agreementsContractStatusOptions,
                params.data?.status
              ),
          },

          {
            field: 'startDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },

          {
            field: 'completion',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              if (params.value === undefined) {
                return '';
              }
              return formatPercent(params.value, undefined, '1.0-0');
            },
          },

          {
            field: 'buyerBroker',
            dataType: 'users',
          },
          {
            field: 'sellerBroker',
            dataType: 'users',
          },
          {
            field: 'buyerBrokerCommission',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency, '1.2-2');
            },
          },
          {
            field: 'sellerBrokerCommission',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency, '1.2-2');
            },
          },
          {
            field: 'brokerCommission',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency, '1.2-2');
            },
          },
          {
            field: 'deferredRevenue',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return getDeferredRevenue(
                params.data?.invoiced,
                params.data?.deferredRevenue,
                params.data?.brokerCommission,
                params.data?.completion
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency, '1.2-2');
            },
          },
          {
            field: 'invoiced',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency, '1.2-2');
            },
          },
        ];
      }

      return [
        {
          field: 'id',
          filter: 'agNumberColumnFilter',
        },
        {
          field: 'company',
          dataType: 'companies',
        },

        {
          field: 'buyerBrokerCommission',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params: any) => {
            return currencyFormatter(params, params.data?.currency, '1.2-2');
          },
        },
        {
          field: 'sellerBrokerCommission',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params: any) => {
            return currencyFormatter(params, params.data?.currency, '1.2-2');
          },
        },
        {
          field: 'brokerCommission',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params: any) => {
            return currencyFormatter(params, params.data?.currency, '1.2-2');
          },
        },
        {
          field: 'deferredRevenue',
          filter: 'agNumberColumnFilter',
          valueGetter: (params: any) => {
            return getDeferredRevenue(
              params.data?.invoiced,
              params.data?.deferredRevenue,
              params.data?.brokerCommission
            );
          },
          valueFormatter: (params: any) => {
            return currencyFormatter(params, params.data?.currency, '1.2-2');
          },
        },
        {
          field: 'targetInUsd',
          headerName: 'Target ($USD)',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params: any) => {
            return currencyFormatter(params, 'USD', '1.0-0');
          },
        },
        {
          field: 'invoiced',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params: any) => {
            return currencyFormatter(params, params.data?.currency, '1.2-2');
          },
        },
      ];
    })
  );

  selectedUserId = new FormControl(this.userService.userId$.value);

  data$: Observable<(AgreementsRow | CompaniesRow)[]> = combineLatest([
    this.dateFormControls.valueChanges.pipe(
      startWith(this.dateFormControls.value)
    ),
    this.selectedUserId.valueChanges.pipe(startWith(this.selectedUserId.value)),
    this.storeService.store.agreements.data$,
    this.storeService.store.invoices.data$,
    this.storeService.store.shipmentFulfillments.data$,
    this.storeService.store.currenciesConversions.data$,
    this.currentRoute$,
    this.storeService.getStoreByKeys$<Companies>('companies'),
  ]).pipe(
    map(
      ([
        dateRange,
        selectedUserId,
        agreements,
        invoices,
        shipmentFulfillments,
        currenciesConversions,
        currentRoute,
        companiesByPk,
      ]) => {
        const dateStart = dateRange?.[0];
        const dateEnd = dateRange?.[1];

        if (!dateStart || !dateEnd) {
          return [];
        }

        console.log('🔥', dateStart, dateEnd);

        const dateStartStr = dateStart.toISOString();
        const dateEndStr = dateEnd.toISOString();

        console.log('🔥', dateStartStr, dateEndStr);

        const processedAgreements: AgreementsRow[] = (
          agreements as any as Agreements[]
        )
          .filter((agreement) => {
            return (
              agreement.startDate >= dateStartStr &&
              agreement.startDate <= dateEndStr &&
              (agreement.buyerUserId === selectedUserId ||
                agreement.sellerUserId === selectedUserId)
            );
          })
          .map((agreement) => {
            return {
              ...agreement,
              invoices: (invoices as Invoices[]).filter(
                (invoice) => invoice.agreementId === agreement.id
              ),
              shipmentFulfillments: (
                shipmentFulfillments as ShipmentFulfillments[]
              ).filter(
                (shipmentFulfillment) =>
                  shipmentFulfillment.agreementId === agreement.id
              ),
            };
          })
          .map((agreement) => {
            const commission =
              this.agreementsService.getSplitCommissionForBrokers(agreement);

            const completion =
              this.agreementsService.getCompletionFromInvoice(agreement);

            const commissionOfSelectedUser = convertCurrencyToUSD(
              currenciesConversions,
              agreement.createdAt,
              agreement.currency as any as string,
              this.agreementsService.getCommissionFromAgreementForSpecificUser(
                agreement,
                selectedUserId ?? undefined
              )
            );

            const x: AgreementsRow = {
              id: agreement.id,
              buyerCompany: agreement.buyerCompanyId as number,
              sellerCompany: agreement.sellerCompanyId as number,
              status: agreement.contractStatus as any,
              buyerBroker: (selectedUserId === agreement.buyerUserId
                ? agreement.buyerUserId
                : null) as string,
              sellerBroker: (selectedUserId === agreement.sellerUserId
                ? agreement.sellerUserId
                : null) as string,
              buyerBrokerCommission: (selectedUserId === agreement.buyerUserId
                ? convertCurrencyToUSD(
                    currenciesConversions,
                    agreement.createdAt,
                    agreement.currency as any as string,
                    commission.buyerBrokerCommission
                  )
                : null) as number,
              sellerBrokerCommission: (selectedUserId === agreement.sellerUserId
                ? convertCurrencyToUSD(
                    currenciesConversions,
                    agreement.createdAt,
                    agreement.currency as any as string,
                    commission.sellerBrokerCommission
                  )
                : null) as number,
              brokerCommission: commissionOfSelectedUser,
              startDate: agreement.startDate,
              deferredRevenue:
                (1 - completion.percentage) * commissionOfSelectedUser,
              invoiced: commissionOfSelectedUser * completion.percentage,
              currency: 'USD' as string,
              agreement: agreement,
              completion:
                this.agreementsService.getCompletionFromAgreement(agreement),
            };

            return x;
          });

        const splitAgreementsWith = (
          agreement: AgreementsRow
        ): CompaniesRow[] => {
          const commissionBuyer =
            (agreement.agreement.commissionBuyer || 0) / 100;
          const commissionSeller =
            (agreement.agreement.commissionSeller || 0) / 100;
          const commissionThirdParty =
            (agreement.agreement.commissionThirdParty || 0) / 100;

          const splittedCommissionByCompany: CompaniesRow[] = [
            {
              ...agreement,
              id: agreement.buyerCompany,
              company: agreement.buyerCompany,
              invoiced: agreement.invoiced * commissionBuyer,
              brokerCommission: agreement.brokerCommission * commissionBuyer,
              sellerBrokerCommission:
                agreement.sellerBrokerCommission * commissionBuyer,
              buyerBrokerCommission:
                agreement.buyerBrokerCommission * commissionBuyer,
              targetInUsd: null,
            },
            {
              ...agreement,
              id: agreement.sellerCompany,
              company: agreement.sellerCompany,
              invoiced: agreement.invoiced * commissionSeller,
              brokerCommission: agreement.brokerCommission * commissionSeller,
              sellerBrokerCommission:
                agreement.sellerBrokerCommission * commissionSeller,
              buyerBrokerCommission:
                agreement.buyerBrokerCommission * commissionSeller,
              targetInUsd: null,
            },
            {
              ...agreement,
              id: agreement.agreement.thirdPartyCompanyId as number,
              company: agreement.agreement.thirdPartyCompanyId as number,
              invoiced: agreement.invoiced * commissionThirdParty,
              brokerCommission:
                agreement.brokerCommission * commissionThirdParty,
              sellerBrokerCommission:
                agreement.sellerBrokerCommission * commissionThirdParty,
              buyerBrokerCommission:
                agreement.buyerBrokerCommission * commissionThirdParty,
              targetInUsd: null,
            },
          ].filter(
            (agreement) =>
              isNumber(agreement.brokerCommission) &&
              agreement.brokerCommission > 0
          );

          return splittedCommissionByCompany;
        };

        if (currentRoute === 'companies') {
          const postProcessedAgreements: CompaniesRow[] = processedAgreements
            .map((agreement) => {
              return splitAgreementsWith(agreement);
            })
            .flatMap((a) => a)
            .reduce((acc, curr) => {
              const existing = acc.find((r) => r.company === curr.company);

              if (existing) {
                existing.brokerCommission += curr.brokerCommission;
                existing.invoiced += curr.invoiced;
                existing.buyerBrokerCommission += curr.buyerBrokerCommission;
                existing.sellerBrokerCommission += curr.sellerBrokerCommission;
                existing.deferredRevenue += curr.deferredRevenue;
              } else {
                acc.push(curr);
              }

              return acc;
            }, [] as CompaniesRow[])
            .map((row) => {
              const target = companiesByPk[row.company].targetObjective;
              const targetObjectiveCurrency =
                companiesByPk[row.company].targetObjectiveCurrency;

              const targetInUsd =
                !target || !targetObjectiveCurrency
                  ? null
                  : convertCurrencyToUSD(
                      currenciesConversions,
                      new Date().toISOString(),
                      targetObjectiveCurrency as string,
                      target as number
                    );

              return {
                ...row,
                targetInUsd,
              };
            });

          return postProcessedAgreements;
        }

        return processedAgreements;
      }
    ),
    shareReplay(1)
  );

  context: Context = {
    footers: [
      {
        label: 'Buyer Br. Com.',
        value: (selectedDatas) =>
          this.data$.pipe(
            map((data) =>
              formatCurrency(
                sum(
                  (selectedDatas?.length ? selectedDatas : data)
                    .filter((x) => x)
                    .map((d) => d.buyerBrokerCommission)
                ),
                undefined,
                'USD',
                '1.0-0'
              )
            )
          ),
      },
      {
        label: 'Seller Br. Com.',
        value: (selectedDatas) =>
          this.data$.pipe(
            map((data) =>
              formatCurrency(
                sum(
                  (selectedDatas?.length ? selectedDatas : data).map(
                    (d) => d.sellerBrokerCommission
                  )
                ),
                undefined,
                'USD',
                '1.0-0'
              )
            )
          ),
      },
      {
        label: 'Broker Com.',
        value: (selectedDatas) =>
          this.data$.pipe(
            map((data) =>
              formatCurrency(
                sum(
                  (selectedDatas?.length ? selectedDatas : data).map(
                    (d) => d.brokerCommission
                  )
                ),
                undefined,
                'USD',
                '1.0-0'
              )
            )
          ),
      },
      {
        label: 'Deferred Rev.',
        value: (selectedDatas) =>
          this.data$.pipe(
            map((data) =>
              formatCurrency(
                sum(
                  (selectedDatas?.length ? selectedDatas : data).map(
                    (d) => d.deferredRevenue
                  )
                ),
                undefined,
                'USD',
                '1.0-0'
              )
            )
          ),
      },
      {
        label: 'Invoiced',
        value: (selectedDatas) =>
          this.data$.pipe(
            map((data) =>
              formatCurrency(
                sum(
                  (selectedDatas?.length ? selectedDatas : data).map(
                    (d) => d.invoiced
                  )
                ),
                undefined,
                'USD',
                '1.0-0'
              )
            )
          ),
      },
    ],
  };

  ngOnInit(): void {
    this.userService.user$
      .pipe(
        map((user) => !(user?.isManager || user?.isAdmin)),
        take(1)
      )
      .subscribe((res) => {
        if (res) {
          this.selectedUserId.disable();
        }
      });
  }
}
